import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';
import { Mousewheel } from 'swiper/modules';

import { Slider } from '@atlas/components';
import { SiteConfigManager, useCasinoNewState } from '@atlas/core';
import { CASINO_TILE_TYPES, CasinoGameTopTile, ChipsItem, Icon, Label } from '@atlas/ui';

import { useIsRtl } from 'hooks/useIsRtl';
import { BASE_PATH } from 'src/common/constants';
import { CDN_TYPE_CMS, imageUrlCDN } from 'src/common/utils';
import requestBuilder from 'src/common/utils/requestBuilder';

import InfoGame from '../components/InfoGame';

const getImageUrl = item => {
  const customImageUrl = item.imageUrl ? encodeURI(imageUrlCDN(item.imageUrl, CDN_TYPE_CMS)) : null;
  return !item.useDefaultIcon && customImageUrl ? customImageUrl : null;
};

const LobbyGameTopCategoriesBlock = ({ blockTitleKey, blocks, imageUrl }) => {
  const swiperRef = useRef(null);
  const prevSlide = useCallback(() => swiperRef.current?.slidePrev(), []);
  const nextSlide = useCallback(() => swiperRef.current?.slideNext(), []);
  const [isShowImg, setIsShowImg] = useState(true);
  const handleError = useCallback(() => setIsShowImg(false), []);

  const [isLocked, setIsLocked] = useState(false);

  const { group } = useCasinoNewState();

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);
  const casinoGameTileType = SiteConfigManager.getConfig(
    'config.config.general.desktopCasinoGameTileView',
    CASINO_TILE_TYPES.TYPE_5,
  );
  const tileTypeClass = {
    'casino-game-top-tile-type_1': casinoGameTileType === CASINO_TILE_TYPES.TYPE_1,
    'casino-game-top-tile-type_2': casinoGameTileType === CASINO_TILE_TYPES.TYPE_2,
    'casino-game-top-tile-type_3': casinoGameTileType === CASINO_TILE_TYPES.TYPE_3,
    'casino-game-top-tile-type_4': casinoGameTileType === CASINO_TILE_TYPES.TYPE_4,
    'casino-game-top-tile-type_5': casinoGameTileType === CASINO_TILE_TYPES.TYPE_5,
    'casino-game-top-tile-type_6': casinoGameTileType === CASINO_TILE_TYPES.TYPE_6,
  };
  const [fetchedGames, setFetchedGames] = useState(null);

  const gameIds = useMemo(() => {
    return (blocks || []).reduce((acc, item) => {
      const id = Number(item.gameId ?? item.id);
      if (!Number.isNaN(id)) acc.push(id);
      return acc;
    }, []);
  }, [blocks]);

  useEffect(() => {
    if (gameIds.length === 0) {
      setFetchedGames([]);
      return;
    }

    const getGames = requestBuilder.create('/api/gs/v2/game', { method: 'GET' });
    getGames
      .execute({ query: { ids: gameIds, group } })
      .then(response => {
        setFetchedGames(response?.games || []);
      })
      .catch(err => {
        console.error('Failed to fetch active games status', err);
        setFetchedGames([]);
      });
  }, [gameIds, group]);

  const finalItems = useMemo(() => {
    const list = blocks || [];
    if (fetchedGames === null) {
      return list.map(item => {
        const gId = item.gameId || item.id;
        return {
          finalImageUrl: getImageUrl(item),
          gId,
          tileGame: {
            id: Number(gId),
            name: item.gameName || '',
            providerDisplayedName: item.providerName || '',
            providerName: item.providerName || '',
          },
        };
      });
    }

    const activeGamesMap = new Map(fetchedGames.map(game => [String(game.id), game]));

    return list
      .filter(item => activeGamesMap.has(String(item.gameId || item.id)))
      .map(item => {
        const gId = item.gameId || item.id;
        const gameData = activeGamesMap.get(String(gId));
        return {
          finalImageUrl: getImageUrl(item),
          gId,
          tileGame: {
            ...gameData,
            providerName: gameData.providerName || gameData.providerDisplayedName,
          },
        };
      });
  }, [blocks, fetchedGames]);

  if (!finalItems || finalItems.length === 0) {
    return null;
  }

  const isPending = fetchedGames === null;

  return (
    <div className="casino-lobby__item lobby-game-top">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={32}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/top-games.png`
                }
                width={32}
              />
            </div>
          )}

          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
        <div className="casino-lobby__right">
          {!isLocked && (
            <>
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-left"
                label={<Icon name="arrow-left" />}
                onClick={prevSlide}
              />
              <ChipsItem
                className="casino-lobby__arrow casino-lobby__arrow-right"
                label={<Icon name="arrow-right" />}
                onClick={nextSlide}
              />
            </>
          )}
        </div>
      </div>
      <div className="casino-lobby__content">
        <div className="lobby-game-top__slider">
          <Slider
            autoplay={false}
            breakpointsBase="container"
            dir={dir}
            direction="horizontal"
            loop={false}
            modules={[Mousewheel]}
            mousewheel={{ forceToAxis: true }}
            onBeforeInit={swiper => {
              swiperRef.current = swiper;
            }}
            onLock={() => setIsLocked(true)}
            onUnlock={() => setIsLocked(false)}
            onUpdate={swiperInstance => setIsLocked(swiperInstance.isLocked)}
            slidesPerView={'auto'}
            spaceBetween={16}
          >
            {finalItems.map(({ finalImageUrl, gId, tileGame }, index) => (
              <Slider.Slide key={`${gId}-${index}`}>
                <CasinoGameTopTile
                  className={cx(index >= 9 && 'casino-game-top-tile-large', tileTypeClass)}
                  index={index}
                  isMobile={false}
                >
                  <InfoGame
                    game={tileGame}
                    imageOverride={finalImageUrl}
                    isLoading={isPending}
                    size="S"
                  />
                </CasinoGameTopTile>
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyGameTopCategoriesBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  blocks: PropTypes.array,
  imageUrl: PropTypes.string,
};

export default LobbyGameTopCategoriesBlock;
